import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Button,
    Typography,
    Box,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';
// Si API_URL ya incluye /api, no lo duplicamos
const BASE_URL = API_URL.endsWith('/api') ? API_URL.replace(/\/api$/, '') : API_URL;

const ValoresEficiencia = () => {
    const [valores, setValores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editando, setEditando] = useState({});
    const [valoresTemp, setValoresTemp] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        cargarValores();
    }, []);

    const cargarValores = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${BASE_URL}/api/valores-eficiencia`);
            
            // Si no hay valores, crear valores por defecto para las máquinas 1-11
            if (response.data.length === 0) {
                const valoresDefault = [
                    { maquinaId: 'Machine1', rpmMaximo: 30 },
                    { maquinaId: 'Machine2', rpmMaximo: 30 },
                    { maquinaId: 'Machine3', rpmMaximo: 30 },
                    { maquinaId: 'Machine4', rpmMaximo: 30 },
                    { maquinaId: 'Machine5', rpmMaximo: 30 },
                    { maquinaId: 'Machine6', rpmMaximo: 30 },
                    { maquinaId: 'Machine7', rpmMaximo: 30 },
                    { maquinaId: 'Machine8', rpmMaximo: 20 },
                    { maquinaId: 'Machine9', rpmMaximo: 300 },
                    { maquinaId: 'Machine10', rpmMaximo: 300 },
                    { maquinaId: 'Machine11', rpmMaximo: 300 }
                ];
                const createResponse = await axios.post(
                    `${BASE_URL}/api/valores-eficiencia/multiple`,
                    { valores: valoresDefault }
                );
                // Ordenar las máquinas por número
                const valoresOrdenados = createResponse.data.sort((a, b) => {
                    const numA = parseInt(a.maquinaId.replace('Machine', ''));
                    const numB = parseInt(b.maquinaId.replace('Machine', ''));
                    return numA - numB;
                });
                setValores(valoresOrdenados);
            } else {
                // Ordenar las máquinas por número
                const valoresOrdenados = response.data.sort((a, b) => {
                    const numA = parseInt(a.maquinaId.replace('Machine', ''));
                    const numB = parseInt(b.maquinaId.replace('Machine', ''));
                    return numA - numB;
                });
                setValores(valoresOrdenados);
            }
        } catch (error) {
            console.error('Error al cargar valores:', error);
            console.error('Detalles del error:', error.response || error.message);
            mostrarSnackbar(`Error al cargar valores: ${error.response?.data?.message || error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const iniciarEdicion = (maquinaId, rpmMaximo) => {
        setEditando({ ...editando, [maquinaId]: true });
        setValoresTemp({ ...valoresTemp, [maquinaId]: rpmMaximo });
    };

    const cancelarEdicion = (maquinaId) => {
        const nuevoEditando = { ...editando };
        delete nuevoEditando[maquinaId];
        setEditando(nuevoEditando);
        
        const nuevoValoresTemp = { ...valoresTemp };
        delete nuevoValoresTemp[maquinaId];
        setValoresTemp(nuevoValoresTemp);
    };

    const guardarValor = async (maquinaId) => {
        const nuevoRpm = parseFloat(valoresTemp[maquinaId]);
        
        if (isNaN(nuevoRpm) || nuevoRpm <= 0) {
            mostrarSnackbar('El RPM máximo debe ser un número mayor a 0', 'error');
            return;
        }

        try {
            await axios.post(
                `${BASE_URL}/api/valores-eficiencia`,
                { maquinaId, rpmMaximo: nuevoRpm }
            );

            // Actualizar el estado local
            setValores(valores.map(v => 
                v.maquinaId === maquinaId 
                    ? { ...v, rpmMaximo: nuevoRpm } 
                    : v
            ));

            cancelarEdicion(maquinaId);
            mostrarSnackbar('Valor actualizado correctamente', 'success');
        } catch (error) {
            console.error('Error al guardar valor:', error);
            mostrarSnackbar('Error al guardar el valor', 'error');
        }
    };

    const handleChangeTemp = (maquinaId, valor) => {
        setValoresTemp({ ...valoresTemp, [maquinaId]: valor });
    };

    const mostrarSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const cerrarSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const calcularEficiencia = (rpm, rpmMaximo) => {
        return ((rpm / rpmMaximo) * 100).toFixed(2);
    };

    if (loading) {
        return (
            <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Paper sx={{ p: 3, backgroundColor: '#1a1a1a', color: '#B8F1FF' }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#B8F1FF', mb: 3, textAlign: 'center' }}>
                    Valores de Eficiencia por Máquina
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="body2" sx={{ color: '#a2aab4' }}>
                        Configure el RPM máximo para cada máquina. La eficiencia se calcula como: 
                        <strong style={{ color: '#B8F1FF' }}> (RPM Actual / RPM Máximo) × 100</strong>
                    </Typography>
                    <Button 
                        variant="outlined" 
                        onClick={cargarValores}
                        disabled={loading}
                        sx={{ 
                            color: '#B8F1FF', 
                            borderColor: '#B8F1FF',
                            '&:hover': { 
                                borderColor: '#22C2E5',
                                backgroundColor: 'rgba(34, 194, 229, 0.1)'
                            }
                        }}
                    >
                        {loading ? 'Cargando...' : 'Recargar'}
                    </Button>
                </Box>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#2a2a2a' }}>
                                <TableCell sx={{ color: '#B8F1FF', fontWeight: 'bold' }}>Máquina</TableCell>
                                <TableCell sx={{ color: '#B8F1FF', fontWeight: 'bold' }}>RPM Máximo</TableCell>
                                <TableCell sx={{ color: '#B8F1FF', fontWeight: 'bold' }}>Ejemplo (RPM=25)</TableCell>
                                <TableCell sx={{ color: '#B8F1FF', fontWeight: 'bold', textAlign: 'center' }}>Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {valores.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} sx={{ textAlign: 'center', color: '#a2aab4', py: 4 }}>
                                        <Typography variant="body1">
                                            No hay valores configurados. Recargando...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                valores.map((valor) => (
                                    <TableRow 
                                        key={valor.maquinaId}
                                        sx={{ 
                                            '&:hover': { backgroundColor: '#222' },
                                            backgroundColor: '#1a1a1a'
                                        }}
                                    >
                                        <TableCell sx={{ color: '#B8F1FF', fontWeight: 'bold' }}>
                                            {valor.maquinaId}
                                        </TableCell>
                                        <TableCell>
                                            {editando[valor.maquinaId] ? (
                                                <TextField
                                                    type="number"
                                                    value={valoresTemp[valor.maquinaId] || ''}
                                                    onChange={(e) => handleChangeTemp(valor.maquinaId, e.target.value)}
                                                    size="small"
                                                    inputProps={{ min: 0.1, step: 0.1 }}
                                                    sx={{
                                                        width: 120,
                                                        '& .MuiInputBase-root': { color: '#B8F1FF' },
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': { borderColor: '#B8F1FF' },
                                                            '&:hover fieldset': { borderColor: '#22C2E5' },
                                                            '&.Mui-focused fieldset': { borderColor: '#22C2E5' }
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <Typography sx={{ color: '#B8F1FF' }}>
                                                    {valor.rpmMaximo} RPM
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ color: '#22C2E5' }}>
                                                {calcularEficiencia(
                                                    25, 
                                                    editando[valor.maquinaId] 
                                                        ? (parseFloat(valoresTemp[valor.maquinaId]) || valor.rpmMaximo)
                                                        : valor.rpmMaximo
                                                )}%
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            {editando[valor.maquinaId] ? (
                                                <>
                                                    <IconButton 
                                                        onClick={() => guardarValor(valor.maquinaId)}
                                                        sx={{ color: '#4caf50' }}
                                                        size="small"
                                                    >
                                                        <SaveIcon />
                                                    </IconButton>
                                                    <IconButton 
                                                        onClick={() => cancelarEdicion(valor.maquinaId)}
                                                        sx={{ color: '#f44336' }}
                                                        size="small"
                                                    >
                                                        <CancelIcon />
                                                    </IconButton>
                                                </>
                                            ) : (
                                                <IconButton 
                                                    onClick={() => iniciarEdicion(valor.maquinaId, valor.rpmMaximo)}
                                                    sx={{ color: '#22C2E5' }}
                                                    size="small"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ mt: 3, p: 2, backgroundColor: '#0d0d0d', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: '#a2aab4' }}>
                        💡 <strong>Nota:</strong> Los cambios se aplicarán inmediatamente en todas las gráficas y cálculos de eficiencia del sistema.
                    </Typography>
                </Box>
            </Paper>

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={4000} 
                onClose={cerrarSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={cerrarSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default ValoresEficiencia;
